import { cn } from "@/lib/utils";
import type { LivePhase } from "@/hooks/use-walkthrough-stream";
import { LiveWalkthroughTimeline } from "./live-walkthrough-timeline";
import { LiveWalkthroughFindings } from "./live-walkthrough-findings";
import { LiveWalkthroughDesigner } from "./live-walkthrough-designer";
import type { useLiveWalkthroughState } from "./use-live-walkthrough-state";

interface LiveWalkthroughTabsProps {
  state: ReturnType<typeof useLiveWalkthroughState>;
  isRunning: boolean;
  isComplete: boolean;
  isErrored: boolean;
  isCancelled: boolean;
  isLocalLive: boolean;
  phase: LivePhase | null;
  streamError?: string | null;
  errorMessage?: string | null;
  activeAction: {
    stepNumber: number;
    actionType: string;
    selector: string | null;
  } | null;
  completion: {
    terminationReason: string;
    verdict: { question: string; answer: string } | null;
    whatWorked: string[];
    openQuestions: string[];
  } | null;
  projectId: string;
  walkthroughId: string;
}

export function LiveWalkthroughTabs({
  state,
  isRunning,
  isComplete,
  isErrored,
  isCancelled,
  isLocalLive,
  phase,
  streamError,
  errorMessage,
  activeAction,
  completion,
  projectId,
  walkthroughId,
}: LiveWalkthroughTabsProps) {
  const showTimeline =
    state.activeTab === "timeline" || state.mobileTab === "timeline";
  const showFindings =
    state.activeTab === "findings" || state.mobileTab === "findings";
  const showDesigner =
    state.activeTab === "designer" || state.mobileTab === "designer";

  return (
    <aside
      aria-label="Walkthrough details"
      className={cn(
        "flex flex-col overflow-y-auto w-full lg:w-[480px] lg:min-w-[360px]",
        state.mobileTab === "screenshot" && "hidden lg:flex",
      )}
      style={state.dividerX != null ? { flex: 1, minWidth: 360 } : undefined}
    >
      {/* Desktop Segmented Tab Switcher */}
      <div className="hidden lg:flex shrink-0 border-b border-border/60 bg-muted/20 p-2">
        <div className="flex w-full rounded-md bg-muted/40 p-1" role="tablist">
          {(
            [
              ["timeline", `Timeline (${state.displaySteps.length})`],
              ["findings", `Findings (${state.unifiedFindings.length})`],
              ["designer", "Designer"],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={state.activeTab === key}
              onClick={() => state.setActiveTab(key)}
              className={cn(
                "flex-1 rounded py-1 text-xs font-semibold transition-colors",
                state.activeTab === key
                  ? "bg-foreground text-background shadow-xs"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Contents */}
      <div className="flex-1">
        {showTimeline && (
          <div
            className={cn(state.mobileTab !== "timeline" && "lg:block hidden")}
          >
            <LiveWalkthroughTimeline
              steps={state.displaySteps}
              activeStepIdx={state.activeStepIdx}
              onSelectStep={state.handleStepChange}
              isLive={state.isLive}
              stepsBehindLive={state.stepsBehindLive}
              onResumeLive={state.handleResumeLive}
              findingsByStep={state.findingsByStep}
              citationsMap={state.citationsMap}
              openPanel={state.openPanel}
              isRunning={isRunning}
              isComplete={isComplete}
              isErrored={isErrored}
              isCancelled={isCancelled}
              isLocalLive={isLocalLive}
              phase={phase}
              errorMessage={streamError || errorMessage}
              activeAction={activeAction}
              completion={completion}
              projectId={projectId}
              walkthroughId={walkthroughId}
            />
          </div>
        )}
        {showFindings && (
          <div
            className={cn(state.mobileTab !== "findings" && "lg:block hidden")}
          >
            <LiveWalkthroughFindings
              findings={state.unifiedFindings}
              activeSeverityFilter={state.activeSeverityFilter}
              onFilterChange={state.setActiveSeverityFilter}
              onJumpToStep={state.handleJumpToStep}
              activeStepNumber={state.displayedStep?.stepNumber}
              projectId={projectId}
            />
          </div>
        )}
        {showDesigner && (
          <div
            className={cn(state.mobileTab !== "designer" && "lg:block hidden")}
          >
            <LiveWalkthroughDesigner
              designerReview={state.effectiveDesignerReview}
              designerFindings={state.designerFindings}
              isRunning={isRunning}
              phase={phase}
              onJumpToStep={state.handleJumpToStep}
            />
          </div>
        )}
      </div>
    </aside>
  );
}
